import { logException } from "@wss/error-tracking";
import { ThrowableStatus } from "@wss/error-tracking/throwable";
import { toError } from "@wss/error-tracking/utils";
import { InternalNotesRecentUpdateResult } from "../ProductDetailPageResult";
import throwableHttpRetry from "~/common/utils/throwableHttpRetry";

const UNACTIONABLE_STATUSES = [401, 408, 502, 503, 504];
async function logError(e: unknown) {
  const error = new Error("Error in getRecentInternalNotesUpdate", {
    cause: toError(e),
  });

  await logException(error);

  return error;
}

export async function getRecentInternalNotesUpdate(
  itemNumber: string,
): Promise<ThrowableStatus<InternalNotesRecentUpdateResult>> {
  const response = await fetch(
    `/api/productdetails/internalnotesupdate?itemNumber=${encodeURIComponent(
      itemNumber,
    )}`,
  );

  if (!response.ok) {
    return {
      isError: true,
      status: response.status,
      error: toError(response.body),
    };
  }

  return {
    isError: false,
    value: await response.json<InternalNotesRecentUpdateResult>(),
  };
}

export async function updateRecentInternalNotes(
  itemNumber: string,
): Promise<ThrowableStatus<InternalNotesRecentUpdateResult>> {
  try {
    const result = await throwableHttpRetry(
      () => getRecentInternalNotesUpdate(itemNumber),
      2,
      200,
      UNACTIONABLE_STATUSES,
    );

    if (!result.isError) {
      return { isError: false, value: result.value };
    }

    const { error, status } = result;

    // skip logging for expected unactionable HTTP status codes, log the rest.
    if (!status || UNACTIONABLE_STATUSES.includes(status)) {
      return {
        isError: true,
        error,
        status,
      };
    }
    return { isError: true, error: await logError(error), status };
  } catch (e) {
    return { isError: true, error: await logError(e), status: null };
  }
}
